/**
 * @file CoachesSection.tsx
 * @summary Section page d'accueil — CoachesSection
 *
 * Bloc de contenu affiché sur la home (`/`). Composant présentationnel utilisant les tokens du design system (Charbon/Or, Bebas Neue/Inter).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { useState, useEffect, useCallback } from "react";
import Section from "@/components/Section";
import { ChevronLeft, ChevronRight } from "lucide-react";
import photoBenbouPortrait from "@/assets/photo-benbou-portrait.webp";
import photoBenbouEchobike from "@/assets/photo-benbou-echobike.webp";
import photoBenbouMicro from "@/assets/photo-benbou-micro.webp";
import photoCedricPortraitNew from "@/assets/photo-cedric-portrait-new.webp";
import photoCedricRope from "@/assets/photo-cedric-rope.webp";
import photoCedricRower from "@/assets/photo-cedric-rower.webp";
import photoCedricDumbbell from "@/assets/photo-cedric-dumbbell.webp";
import photoCedricRopeclimb from "@/assets/photo-cedric-ropeclimb.webp";
import photoCedricRopeclimbNew from "@/assets/photo-cedric-ropeclimb-new.webp";
import photoCoachOrni from "@/assets/photo-coach-orni.webp";
import photoCoachDavid from "@/assets/photo-coach-david.webp";
import photoCoachPerrine from "@/assets/photo-coach-perrine.webp";

const coaches = [
  {
    name: "Anthony Bendahmane",
    nickname: "Benbou",
    role: "Co-gérant & Coach",
    diplomas: "Master STAPS · CrossFit Level 1",
    img: photoBenbouPortrait,
    satellites: [photoBenbouEchobike, photoBenbouMicro],
    desc: "Ancien rugbyman de haut niveau en équipe jeune, Anthony (dit « Benbou ») est tombé amoureux du CrossFit. Diplômé d'un Master STAPS et certifié CrossFit Level 1, il t'accompagne avec passion, exigence et bienveillance. Son énergie est très communicative !",
  },
  {
    name: "Cédric Bastien",
    nickname: "Cédric",
    role: "Co-gérant & Coach",
    diplomas: "BPJEPS · CrossFit Level 1",
    img: photoCedricRopeclimbNew,
    satellites: [photoCedricPortraitNew, photoCedricRopeclimb, photoCedricRower, photoCedricDumbbell],
    desc: "Également ancien rugbyman, Cédric a co-fondé Chistera avec Anthony par passion commune pour l'effort et la transmission. Diplômé BPJEPS et certifié CrossFit Level 1, il allie technique et bonne humeur pour te faire progresser.",
  },
  {
    name: "Orni",
    nickname: "Orni",
    role: "Coach CrossFit",
    diplomas: "Double BPJEPS APT & AF · Préparation mentale · Formation infirmière",
    img: photoCoachOrni,
    satellites: [],
    desc: "Concentré d'énergie, de bienveillance et de mental d'acier. Double BPJEPS (Activité Physique pour Tous & Activité de la Forme), diplôme de préparation mentale et 2 ans de formation infirmière : elle sait prendre soin de toi… et te faire bosser. Ancienne footballeuse, amoureuse du grand air et des runs qui piquent. Playlist imprévisible, de Theodora à Bad Bunny, jusqu'au gros boom boom qui te fait arracher un PR. Passionnée, fière et battante : elle va te faire progresser et souffrir, toujours avec le sourire.",
  },
  {
    name: "Perrine",
    nickname: "Perrine",
    role: "Coach CrossFit",
    diplomas: "Coach diplômée CrossFit",
    img: photoCoachPerrine,
    satellites: [],
    desc: "Le sourire qui fait plier même les burpees. Une dose de bonne humeur XXL, une énergie solaire et une exigence qui ne laisse rien passer. Toujours aux petits soins, elle veille sur toi du début à la fin de la séance. Coach exigeante car elle sait exactement de quoi tu es capable (et un peu plus encore). Sa spécialité : te faire faire la rep de plus, celle qui change tout. Avec elle tu vas souffler, transpirer… mais surtout progresser et kiffer.",
  },
  {
    name: "David",
    nickname: "Dadou",
    role: "Coach CrossFit",
    diplomas: "Ancien militaire · Coach CrossFit",
    img: photoCoachDavid,
    satellites: [],
    desc: "Mental de guerrier, discipline d'acier et motivation contagieuse. Ancien militaire — autant dire que le mot abandon n'existe pas dans son vocabulaire. Coach exigeant mais toujours là pour te pousser à donner plus que ce que tu pensais possible. Amoureux des défis qui piquent et des compétitions les plus dures, c'est clairement son terrain de jeu. Avec lui tu vas transpirer, parfois douter… mais surtout progresser.",
  },
];



function AutoCarousel({ images }: { images: { src: string; alt: string }[] }) {
  const [current, setCurrent] = useState(0);
  const visibleCount = 3;

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % images.length);
  }, [images.length]);

  const prev = () => {
    setCurrent((c) => (c - 1 + images.length) % images.length);
  };

  useEffect(() => {
    const interval = setInterval(next, 3000);
    return () => clearInterval(interval);
  }, [next]);

  const getVisible = () => {
    const result = [];
    for (let i = 0; i < visibleCount; i++) {
      result.push(images[(current + i) % images.length]);
    }
    return result;
  };

  return (
    <div className="relative">
      <div className="flex gap-3 overflow-hidden">
        {getVisible().map((img, i) => (
          <div key={`${current}-${i}`} className="flex-1 aspect-square rounded-lg overflow-hidden">
            <img src={img.src} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
          </div>
        ))}
      </div>
      <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-charcoal/70 text-white rounded-full p-1.5 hover:bg-charcoal transition-colors" aria-label="Précédent">
        <ChevronLeft size={18} />
      </button>
      <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-charcoal/70 text-white rounded-full p-1.5 hover:bg-charcoal transition-colors" aria-label="Suivant">
        <ChevronRight size={18} />
      </button>
      {/* Dots */}
      <div className="flex justify-center gap-1.5 mt-3">
        {images.map((_, i) => (
          <button key={i} onClick={() => setCurrent(i)} className={`w-2 h-2 rounded-full transition-colors ${i === current ? "bg-gold" : "bg-border"}`} aria-label={`Photo ${i + 1}`} />
        ))}
      </div>
    </div>
  );
}

export default function CoachesSection() {
  return (
    <Section id="coachs">
      <div className="text-center mb-12">
        <span className="inline-block text-sm font-semibold text-gold tracking-wider mb-2 font-body normal-case">Anciens rugbymen, CrossFitters dans l'âme</span>
        <h2 className="text-4xl md:text-5xl text-foreground">Les coachs</h2>
      </div>

      {/* Intro story */}
      <p className="text-center max-w-2xl mx-auto text-muted-foreground leading-relaxed mb-12 font-body normal-case text-sm">
        Des affinités sportives, une vision entrepreneuriale commune, un goût de l'effort singulier et un désir de transmission dans la bienveillance — CrossFit Chistera est né en décembre 2022.
        C'est autant un lieu d'apprentissage et de dépassement de soi qu'un lieu de vie où chacun trouve sa place
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
        {coaches.map((c) => (
          <div key={c.name} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 group">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              {/* Satellite bubbles — bigger */}
              {c.satellites.length > 0 && (
                <div className="absolute bottom-3 right-3 flex gap-2">
                  {c.satellites.map((sat, i) => (
                    <div key={i} className="w-14 h-14 md:w-18 md:h-18 rounded-full overflow-hidden border-2 border-gold shadow-lg">
                      <img src={sat} alt={`${c.nickname} action ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6">
              <h3 className="text-2xl text-foreground">{c.name}</h3>
              <span className="text-sm font-semibold text-gold font-body normal-case">{c.role}</span>
              <p className="text-xs text-muted-foreground font-body normal-case mt-1">{c.diplomas}</p>
              <p className="text-sm text-muted-foreground mt-3 font-body normal-case leading-relaxed">{c.desc}</p>
            </div>
          </div>
        ))}
      </div>

    </Section>
  );
}
